/**
 * 🏷️ BADGE STATUT FACTURE LOGISTICS
 * ==================================
 * Composant réutilisable pour afficher le statut de paiement d'une facture
 *
 * Fonctionnalités:
 * - Badges colorés avec icônes selon statut
 * - Tailles configurables (small, default, large)
 * - Textes en français avec traductions claires
 * - Design cohérent avec Ant Design
 *
 * Statuts supportés:
 * - paid: Payée (vert) ✅
 * - partial: Partiellement payée (orange) 🕐
 * - unpaid: Non payée (gris) ⏳
 * - overdue: En retard (rouge) ⚠️
 *
 * Pattern source:
 * - Inspiré de: src/gestion-souscription/components/InvoiceStatusBadge.tsx
 * - Adapté pour: Logistics (même logique, contexte différent)
 *
 * @example
 * <InvoiceStatusBadge status="paid" size="default" />
 * <InvoiceStatusBadge status="partial" size="small" />
 */

import React from 'react';
import { Tag } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';

interface InvoiceStatusBadgeProps {
  status: 'paid' | 'partial' | 'unpaid' | 'overdue';
  size?: 'small' | 'default' | 'large';
  showIcon?: boolean;
}

const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  size = 'default',
  showIcon = true,
}) => {
  // Configuration des statuts avec couleurs, icônes et labels
  const statusConfig: Record<
    string,
    { color: string; icon: React.ReactNode; label: string }
  > = {
    paid: {
      color: 'success',
      icon: <CheckCircleOutlined />,
      label: 'Payée',
    },
    partial: {
      color: 'warning',
      icon: <ClockCircleOutlined />,
      label: 'Partiellement payée',
    },
    unpaid: {
      color: 'default',
      icon: <ClockCircleOutlined />,
      label: 'Non payée',
    },
    overdue: {
      color: 'error',
      icon: <ExclamationCircleOutlined />,
      label: 'En retard',
    },
  };

  // Récupérer la config du statut (fallback sur unpaid si inconnu)
  const config = statusConfig[status] || statusConfig.unpaid;

  // Styles selon la taille
  const sizeStyles = {
    small: {
      fontSize: '12px',
      padding: '2px 8px',
    },
    default: {
      fontSize: '14px',
      padding: '4px 12px',
    },
    large: {
      fontSize: '16px',
      padding: '6px 16px',
    },
  };

  return (
    <Tag
      color={config.color}
      icon={showIcon ? config.icon : undefined}
      style={sizeStyles[size]}
    >
      {config.label}
    </Tag>
  );
};

export default InvoiceStatusBadge;
